import { useState } from 'react';
import { Search, Link2, Loader2 } from 'lucide-react';
import { mediaApi, fileRequestApi } from '../lib/api';

interface MediaHit {
  id: string;
  original_filename: string;
  file_type?: string;
  file_size?: number;
  duration?: number | null;
  editor_name?: string | null;
}

/**
 * Reference/reuse an EXISTING media file into this request (no re-upload).
 * Self-contained so it can drop into the details modal without touching its
 * state. On success it calls onAttached() so the parent can refresh its list.
 */
export function ReferenceExistingFilePanel({
  requestId,
  onAttached,
}: {
  requestId: string;
  onAttached: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<MediaHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [attachingId, setAttachingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const runSearch = async () => {
    if (!term.trim()) return;
    setSearching(true);
    setMessage(null);
    try {
      const res = await mediaApi.getAll({ search: term.trim(), limit: 10 });
      const items: MediaHit[] = res.data?.data?.files || res.data?.data || res.data?.files || [];
      setResults(Array.isArray(items) ? items : []);
    } catch (e: any) {
      setMessage(e?.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const attach = async (fileId: string, name: string) => {
    setAttachingId(fileId);
    setMessage(null);
    try {
      await fileRequestApi.attachExisting(requestId, fileId);
      setMessage(`Referenced "${name}" into this request.`);
      setResults([]);
      setTerm('');
      onAttached();
    } catch (e: any) {
      setMessage(e?.response?.data?.error || 'Failed to attach file');
    } finally {
      setAttachingId(null);
    }
  };

  return (
    <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
      >
        <Link2 className="w-4 h-4" />
        {open ? 'Hide "use existing file"' : 'Use an existing file (no re-upload)'}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                placeholder="Search by file name, e.g. S2-CA-BROLL-HAM"
                className="w-full pl-8 pr-2 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>
            <button
              type="button"
              onClick={runSearch}
              disabled={searching || !term.trim()}
              className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white disabled:opacity-50"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </button>
          </div>

          {message && <p className="text-xs text-gray-600 dark:text-gray-300">{message}</p>}

          {results.length > 0 && (
            <ul className="max-h-56 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
              {results.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-2 px-2 py-1.5">
                  <span className="font-mono text-xs truncate max-w-[16rem]" title={f.original_filename}>
                    {f.original_filename}
                    {f.editor_name ? <span className="text-gray-400"> · {f.editor_name}</span> : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => attach(f.id, f.original_filename)}
                    disabled={attachingId === f.id}
                    className="px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 disabled:opacity-50"
                  >
                    {attachingId === f.id ? 'Attaching…' : 'Attach'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
